import React, { useEffect, useRef, useState } from 'react';
import firebase from '../firebaseConfig';
import appIcon from '../app-icon.png';
import LoginBackground from './LoginBackground';
import User from '../models/User';
import { uuid } from '../utils';
import { track as trackEvent, signupMethod } from '../analytics';

// New-account gate. Mirrors iOS EnterNameVC + CreateArtistVC: a fresh
// account must supply a first name (last name optional) and create an
// artist before reaching the app. Rendered on the login cassette background.

const capitalize = (w) =>
    w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w;

const Onboarding = ({ user, onComplete }) => {
    const needsName = !user.firstName?.trim();

    const [step, setStep] = useState(needsName ? 'name' : 'artist');
    const [fullName, setFullName] = useState(
        [user.firstName, user.lastName].filter((p) => p && p.trim()).join(' '),
    );
    const [artistName, setArtistName] = useState('');
    const [suggestedArtist, setSuggestedArtist] = useState(
        (user.firstName || '').trim().toLowerCase(),
    );
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        setError('');
        const id = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => window.clearTimeout(id);
    }, [step]);

    const saveName = async (e) => {
        e.preventDefault();
        if (busy) return;
        const parts = fullName.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) {
            setError('Please enter your name.');
            return;
        }
        const first = capitalize(parts[0]);
        const last = parts.slice(1).map(capitalize).join(' ');
        setBusy(true);
        setError('');
        try {
            await User.setName(firebase.auth().currentUser.uid, first, last);
            setSuggestedArtist(first.toLowerCase());
            setBusy(false);
            setStep('artist');
        } catch (err) {
            setError(err?.message || 'Could not save your name. Try again.');
            setBusy(false);
        }
    };

    const createArtist = async (e) => {
        e.preventDefault();
        if (busy) return;
        const name = artistName.trim() || suggestedArtist.trim();
        if (!name) {
            setError('Please enter an artist name.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const uid = firebase.auth().currentUser.uid;
            const fn = firebase.functions().httpsCallable('createArtist');
            await fn({ uId: uid, artistId: uuid(), artistName: name });
            trackEvent('sign_up', {
                signup_with_artist: true,
                method: signupMethod(firebase.auth().currentUser),
            });
            onComplete();
        } catch (err) {
            setError(err?.message || 'Could not create your artist. Try again.');
            setBusy(false);
        }
    };

    const signOut = () => { firebase.auth().signOut(); };

    const onName = step === 'name';

    return (
        <div className="full-page login-page">
            <LoginBackground />
            <div className="login-card onboarding-card">
                <div className="login-brand">
                    <img src={appIcon} alt="" className="login-brand-icon" />
                    <span className="login-brand-name">demodeck</span>
                </div>

                {needsName && (
                    <div className="onboarding-dots">
                        <span className={`onboarding-dot ${onName ? 'active' : ''}`} />
                        <span className={`onboarding-dot ${!onName ? 'active' : ''}`} />
                    </div>
                )}

                {onName ? (
                    <form className="onboarding-form" onSubmit={saveName}>
                        <h2 className="onboarding-title">What's your name?</h2>
                        <p className="onboarding-subtitle">
                            So your bandmates know who's who.
                        </p>
                        <input
                            ref={inputRef}
                            className="onboarding-input"
                            type="text"
                            placeholder="Name"
                            autoComplete="name"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            disabled={busy}
                        />
                        <button type="submit" className="onboarding-submit" disabled={busy}>
                            {busy ? 'Saving…' : 'Continue'}
                        </button>
                    </form>
                ) : (
                    <form className="onboarding-form" onSubmit={createArtist}>
                        <h2 className="onboarding-title">Create your artist</h2>
                        <p className="onboarding-subtitle">
                            An artist is your workspace for projects and demos.
                        </p>
                        <input
                            ref={inputRef}
                            className="onboarding-input"
                            type="text"
                            placeholder={suggestedArtist || 'Artist name'}
                            value={artistName}
                            onChange={(e) => setArtistName(e.target.value)}
                            disabled={busy}
                        />
                        <button type="submit" className="onboarding-submit" disabled={busy}>
                            {busy ? 'Creating…' : 'Create artist'}
                        </button>
                    </form>
                )}

                {error && <p className="onboarding-error">{error}</p>}

                <button type="button" className="onboarding-signout" onClick={signOut}>
                    Sign out
                </button>
            </div>
        </div>
    );
};

export default Onboarding;
